import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Calendar, MapPin, Phone, Mail, Lock, 
  Eye, EyeOff, Facebook, Instagram, CheckCircle2, 
  Briefcase, Star, Building, CheckSquare, Shield,
  Camera, Info, MessageSquare, Award, Clock, UserCheck,
  Loader, AlertCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../ui/toast/use-toast';
import LocationAutocomplete from '../LocationAutocomplete';
import BaseRegister from '../register/BaseRegister';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const SellerRegister = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const totalSteps = 6; // Reducido de 7 a 6 pasos

  // Estados
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [devCode, setDevCode] = useState(null);

  // Estados de verificación
  const [verificationState, setVerificationState] = useState({
    loading: false,
    codeSent: false,
    error: null
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  // Datos del formulario
  const [formData, setFormData] = useState({
    userType: 'vendedor',
    nombre: '',
    fechaNacimiento: '',
    genero: '',
    fotoPerfil: null,
    fotoPreview: null,
    ubicacion: '',
    ubicacionData: null,
    email: '',
    emailVerified: false,
    telefono: '', // Mantenemos el campo pero sin validación SMS
    prefijo_telefono: '+51',
    whatsapp: '',
    facebook: '',
    instagram: '',
    sobre_mi: '',
    experiencia: '',
    anos_experiencia: '',
    servicios: [],
    password: '',
    confirmPassword: '',
    slogan: ''
  });

  // Helper function for error handling fetch requests
  const handleFetchError = (error, defaultMessage = 'Error de conexión') => {
    console.error('Network Error:', error);
    
    const errorMessage = error.message === 'Failed to fetch' 
      ? defaultMessage 
      : error.message;

    toast({
      title: "Error de Conexión",
      description: errorMessage,
      variant: "destructive"
    });

    return null;
  };

  // Timer para reenvío de código
  useEffect(() => {
    let timer;
    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            setResendDisabled(false);
            return 0;
          }
          return newValue;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timeLeft]);

  // Manejadores de cambios en formulario
  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    
    // Control especial para fecha de nacimiento
    if (name === 'fechaNacimiento') {
      const inputDate = new Date(value);
      const today = new Date();
      
      // Calcular edad
      let age = today.getFullYear() - inputDate.getFullYear();
      const monthDiff = today.getMonth() - inputDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < inputDate.getDate())) {
        age--;
      }
      
      // Validar si es menor de 18 años
      if (age < 18) {
        setErrors(prev => ({
          ...prev,
          fechaNacimiento: 'Debes tener al menos 18 años para registrarte'
        }));
      } else {
        // Si es mayor de 18, eliminar el error si existe
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.fechaNacimiento;
          return newErrors;
        });
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpiar errores al cambiar el valor
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Validación básica para teléfono sin verificar disponibilidad
    if (name === 'telefono' && value && value.length !== 9) {
      setErrors(prev => ({
        ...prev,
        telefono: 'El teléfono debe tener 9 dígitos'
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, fotoPerfil: 'La imagen no debe superar 5MB' }));
        return;
      }

      // Mostrar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ 
          ...prev, 
          fotoPerfil: file,
          fotoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);

      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.fotoPerfil;
        return newErrors;
      });
    }
  };

  const handleLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      ubicacion: `${location.city}, ${location.province}, ${location.country}`,
      ciudad: location.city,
      provincia: location.province,
      pais: location.country
    }));
    
    if (errors.location) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.location;
        return newErrors;
      });
    }
  };

  const handleServiceChange = (service) => {
    setFormData(prev => {
      const newServices = prev.servicios.includes(service)
        ? prev.servicios.filter(s => s !== service)
        : [...prev.servicios, service];
      return { ...prev, servicios: newServices };
    });
  };

  const verifyEmailCode = async (code) => {
    if (code.length !== 6) return;

    try {
      setVerificationState(prev => ({ ...prev, loading: true }));

      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/verification/verify-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          code: code,
          userType: formData.userType
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Código inválido');
      }

      const data = await response.json();

      setFormData(prev => ({ ...prev, emailVerified: true }));
      // Clear any email-related errors
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.email;
        return newErrors;
      });
      
      setVerificationState({
        loading: false,
        codeSent: false,
        error: null
      });

      toast({
        title: "Email verificado",
        description: "Tu email ha sido verificado correctamente",
      });

      handleNext();

    } catch (error) {
      console.error('Error en verificación:', error);
      setVerificationState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));

      toast({
        title: "Error",
        description: error.message || "Error al verificar el código",
        variant: "destructive",
      });
    }
  };

  // Componente para mostrar el estado de la verificación
  const VerificationStatus = () => {
    if (verificationState.loading) {
      return (
        <div className="flex items-center justify-center space-x-2 text-blue-500">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          <span>Enviando código...</span>
        </div>
      );
    }
    
    if (verificationState.error) {
      return (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0"/>
          <span>{verificationState.error}</span>
        </div>
      );
    }
    
    if (verificationState.codeSent) {
      return (
        <div className="bg-blue-50 text-blue-700 p-3 rounded-lg">
          <p>Te hemos enviado un código de verificación por email.</p>
          <p className="mt-1 text-sm">
            Tiempo restante: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
          </p>
        </div>
      );
    }
    
    return null;
  };

  const requestVerificationCode = async () => {
    if (!formData.email) {
      setErrors(prev => ({ ...prev, email: 'El email es requerido' }));
      return;
    }

    // Validación básica de formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Formato de email inválido' }));
      return;
    }

    // Indicamos que estamos verificando
    setVerificationState({
      loading: true,
      codeSent: false,
      error: null
    });

    try {
      // Crear un nuevo AbortController para toda la operación
      const controller = new AbortController();
      
      // Configurar un timeout más largo
      const timeoutId = setTimeout(() => {
        controller.abort('La solicitud tardó demasiado');
      }, 15000); // 15 segundos

      // Verificar primero si el email ya existe
      const checkResponse = await fetch(`https://backend-production-1786.up.railway.app/api/auth/check-email/${formData.email.trim()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        signal: controller.signal
      });

      if (!checkResponse.ok) {
        clearTimeout(timeoutId);
        const errorData = await checkResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al verificar email');
      }

      const checkData = await checkResponse.json();
      console.log('Respuesta de verificación de email:', checkData);
      
      // Si el email ya existe según la respuesta del servidor
      if (checkData.exists) {
        clearTimeout(timeoutId);
        setErrors(prev => ({
          ...prev,
          email: 'Este email ya está registrado'
        }));
        
        setVerificationState({
          loading: false,
          codeSent: false,
          error: 'Este email ya está registrado. Por favor utiliza otro email o inicia sesión.'
        });
        
        return;
      }

      console.log('Iniciando solicitud de código de verificación para:', formData.email);

      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/verification/request-email-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          userType: formData.userType
        }),
        signal: controller.signal
      });

      // Limpiar el timeout
      clearTimeout(timeoutId);

      console.log('Respuesta del servidor:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error en respuesta:', errorData);
        
        // Si el servidor indica que el email ya está registrado
        if (errorData.error === 'EMAIL_EXISTS' || 
            errorData.message?.includes('ya está registrado') || 
            response.status === 409) {
          throw new Error('Este email ya está registrado. Por favor utiliza otro email o inicia sesión.');
        } else {
          throw new Error(errorData.error || errorData.message || 'Error en la verificación');
        }
      }

      const data = await response.json();
      console.log('Código enviado correctamente');

      // Todo salió bien, actualizamos el estado
      setVerificationState({
        loading: false,
        codeSent: true,
        error: null
      });

      toast({
        title: "Código enviado",
        description: "Revisa tu correo electrónico para el código de verificación",
      });

      // Iniciar temporizador para reenvío
      setTimeLeft(600); // 10 minutos
      setResendDisabled(true);

      // En desarrollo, mostrar el código
      if (process.env.NODE_ENV === 'development' && data.code) {
        console.log('Código de verificación (desarrollo):', data.code);
        setDevCode(data.code);
      }

    } catch (error) {
      console.error('Error al solicitar código:', error.message);
      
      // Manejar específicamente errores de cancelación
      if (error.name === 'AbortError') {
        toast({
          title: "Tiempo de espera agotado",
          description: "La solicitud tardó demasiado. Por favor, verifica tu conexión e inténtalo de nuevo.",
          variant: "destructive"
        });
      } else {
        // Manejo detallado de errores de red
        const errorMessage = error.message || 'No se pudo enviar el código de verificación';
        
        // Si el mensaje indica que el email ya está registrado
        if (errorMessage.includes('ya está registrado')) {
          setErrors(prev => ({
            ...prev,
            email: 'Este email ya está registrado'
          }));
        }
        
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }

      // Resetear estado de carga
      setVerificationState({
        loading: false,
        codeSent: false,
        error: error.message || 'Error al enviar código'
      });
    }
  };

  // Funciones de navegación
  const validateStep = () => {
    const newErrors = {};

    switch (currentStep) {
      case 1: // Email verification
        if (!formData.email) {
          newErrors.email = 'El email es requerido';
        } else if (!formData.emailVerified) {
          newErrors.email = 'Debes verificar tu email antes de continuar';
        }
        break;

      case 2: // Password
        if (!formData.password) {
          newErrors.password = 'La contraseña es requerida';
        } else if (formData.password.length < 8) {
          newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }
        break;

      case 3: // Personal Info
        if (!formData.nombre) newErrors.nombre = 'El nombre es requerido';
        if (!formData.fechaNacimiento) {
          newErrors.fechaNacimiento = 'La fecha es requerida';
        } else {
          // Validar edad mínima
          const inputDate = new Date(formData.fechaNacimiento);
          const today = new Date();
          
          // Calcular edad
          let age = today.getFullYear() - inputDate.getFullYear();
          const monthDiff = today.getMonth() - inputDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < inputDate.getDate())) {
            age--;
          }
          
          // Validar si es menor de 18 años
          if (age < 18) {
            newErrors.fechaNacimiento = 'Debes tener al menos 18 años para registrarte';
          }
        }
        if (!formData.genero) newErrors.genero = 'El género es requerido';
        if (!formData.ubicacion) {
          newErrors.location = 'Por favor seleccione una ubicación válida';
        }
        if (!formData.fotoPerfil) newErrors.fotoPerfil = 'La foto de perfil es requerida';
        break;

      case 4: // Seller Social Info
        if (!formData.sobre_mi) newErrors.sobre_mi = 'La descripción sobre ti es requerida';
        break;

      case 5: // Experience & Slogan
        if (!formData.slogan) newErrors.slogan = 'El slogan es requerido';
        if (!formData.experiencia) newErrors.experiencia = 'La experiencia es requerida';
        if (!formData.anos_experiencia) {
          newErrors.anos_experiencia = 'Los años de experiencia son requeridos';
        }
        break;

      case 6: // Services & Terms (último paso)
        if (formData.servicios.length === 0) {
          newErrors.servicios = 'Debe seleccionar al menos un servicio';
        }
        if (formData.servicios.length > 6) {
          newErrors.servicios = 'No puede seleccionar más de 6 servicios';
        }
        
        // Validar teléfono (opcional pero si se proporciona debe ser válido)
        if (formData.telefono && formData.telefono.length !== 9) {
          newErrors.telefono = 'El teléfono debe tener 9 dígitos';
        }
        
        if (!acceptedTerms) {
          newErrors.terms = 'Debe aceptar los Términos y Condiciones';
        }
        if (!acceptedPrivacy) {
          newErrors.privacy = 'Debe aceptar la Política de Privacidad';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setErrors({});
  };

  const handleFinish = async () => {
    if (!validateStep()) return;

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      
      // Preparar los datos para enviar
      const dataToSend = { ...formData };
      
      // Eliminar campos que no queremos enviar
      delete dataToSend.confirmPassword;
      delete dataToSend.fotoPreview;
      
      // Procesar foto de perfil si existe
      if (dataToSend.fotoPerfil instanceof File) {
        // Importante: usar 'fotoPerfil' como nombre del campo para que coincida con el backend
        formDataToSend.append('fotoPerfil', dataToSend.fotoPerfil);
        
        // Eliminamos esto para evitar duplicación
        delete dataToSend.fotoPerfil;
      }
      
      // Convertir array de servicios a JSON string si no está vacío
      if (dataToSend.servicios && dataToSend.servicios.length > 0) {
        formDataToSend.append('servicios', JSON.stringify(dataToSend.servicios));
        delete dataToSend.servicios;
      }
      
      // Agregar el resto de campos al FormData
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] !== null && dataToSend[key] !== undefined) {
          formDataToSend.append(key, dataToSend[key]);
        }
      });

      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/register/seller', {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error en el registro');
      }

      // Mostrar mensaje de éxito
      toast({
        title: "Registro exitoso",
        description: "¡Te has registrado correctamente! Por favor revisa tu correo electrónico para un mensaje de bienvenida.",
        variant: "success",
      });

      // Login automático
      if (data.token && data.user) {
        login(data.token, data.user);
        navigate('/mi_perfil');
      } else {
        throw new Error('Datos de autenticación inválidos');
      }
    } catch (error) {
      console.error('Error en registro:', error);
      setErrors({ submit: error.message });
      
      toast({
        title: "Error en el registro",
        description: error.message || "No se pudo completar el registro",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Renderizado de pasos
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return renderEmailVerification();
      case 2:
        return renderPasswordStep();
      case 3:
        return renderPersonalInfo();
      case 4:
        return renderSellerSocialInfo();
      case 5:
        return renderExperienceAndSlogan();
      case 6:
        return renderServicesAndTerms(); // Último paso combinado
      default:
        return null;
    }
  };

  // Componentes de renderizado de pasos
  const renderEmailVerification = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Verificación de Email
      </motion.h3>

      {formData.emailVerified ? (
        <motion.div variants={fadeInUp} className="bg-green-50 p-4 rounded-xl border border-green-200">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-green-500" />
            <div>
              <h4 className="font-medium text-green-800">Email verificado correctamente</h4>
              <p className="text-green-600 text-sm">{formData.email}</p>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div variants={fadeInUp} className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleInputChange}
            className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl transition-colors duration-300 ${
              formData.emailVerified ? 'border-green-500 bg-green-50' : errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200'
            }`}
            disabled={formData.emailVerified || verificationState.loading}
          />
          {formData.emailVerified && (
            <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500" size={20} />
          )}
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
        </motion.div>
      )}

      {/* Estado de verificación */}
      <motion.div variants={fadeInUp}>
        <VerificationStatus />
      </motion.div>

      {/* Código en modo desarrollo */}
      {process.env.NODE_ENV === 'development' && devCode && verificationState.codeSent && (
        <motion.div 
          variants={fadeInUp}
          className="bg-indigo-50 border border-indigo-200 p-3 rounded-lg"
        >
          <p className="text-indigo-800 font-medium">Código de desarrollo: {devCode}</p>
          <p className="text-xs text-indigo-600">Este código solo se muestra en modo desarrollo</p>
        </motion.div>
      )}

      {!formData.emailVerified && (
        <>
          <motion.button
            variants={fadeInUp}
            onClick={requestVerificationCode}
            disabled={verificationState.loading || resendDisabled || !formData.email}
            whileHover={!verificationState.loading && !resendDisabled && formData.email ? { scale: 1.02 } : {}}
            whileTap={!verificationState.loading && !resendDisabled && formData.email ? { scale: 0.98 } : {}}
            className={`w-full py-3 rounded-xl transition-all duration-300
              ${verificationState.loading || resendDisabled || !formData.email
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md hover:shadow-lg'
              }`}
          >
            {verificationState.loading ? (
              <div className="flex items-center justify-center">
                <span className="mr-2">Enviando...</span>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              </div>
            ) : verificationState.codeSent ? (
              'Reenviar código'
            ) : (
              'Enviar código de verificación'
            )}
          </motion.button>

          {verificationState.codeSent && (
            <motion.div 
              variants={fadeInUp} 
              className="mt-4 space-y-4"
            >
              <div className="bg-white p-4 rounded-xl border border-gray-200">
                <p className="text-sm text-gray-700 mb-3 text-center">
                  Ingresa el código de 6 dígitos que enviamos a tu email
                </p>
                <input
                  type="text"
                  placeholder="Código de verificación"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                  }}
                  className="w-full px-4 py-3 border-2 rounded-xl text-center text-lg tracking-wider focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  maxLength={6}
                />
                
                <button
                  onClick={() => {
                    if (verificationCode.length === 6) {
                      verifyEmailCode(verificationCode);
                    }
                  }}
                  disabled={verificationCode.length !== 6 || verificationState.loading}
                  className={`w-full mt-3 py-2 rounded-lg transition-colors ${
                    verificationCode.length !== 6 || verificationState.loading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-indigo-500 text-white hover:bg-indigo-600'
                  }`}
                >
                  {verificationState.loading ? 'Verificando...' : 'Verificar código'}
                </button>
              </div>
              {timeLeft > 0 && (
                <p className="text-sm text-gray-500 text-center">
                  Podrás reenviar el código en: <span className="font-medium">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
                </p>
              )}
            </motion.div>
          )}
        </>
      )}
      
      <motion.div 
        variants={fadeInUp}
        className="bg-indigo-50 rounded-xl p-4 border border-indigo-100"
      >
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-700">
            La verificación de tu email nos ayuda a proteger tu cuenta y a mantener segura nuestra plataforma. También nos permite enviarte notificaciones importantes sobre tus propiedades.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Renderizado del paso de contraseña
  const renderPasswordStep = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Configura tu Contraseña
      </motion.h3>

      <motion.div variants={fadeInUp} className="relative">
        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Contraseña"
          name="password"
          value={formData.password}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-12 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.password ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-indigo-500 transition-colors"
        >
          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
        {errors.password && (
          <p className="text-sm text-red-500 mt-1">{errors.password}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type={showConfirmPassword ? "text" : "password"}
          placeholder="Confirmar Contraseña"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-12 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-indigo-500 transition-colors"
        >
          {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
        {errors.confirmPassword && (
          <p className="text-sm text-red-500 mt-1">{errors.confirmPassword}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="space-y-3 mt-4 bg-white p-4 rounded-xl border border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <Shield className="w-4 h-4 text-indigo-500" />
          Requisitos de seguridad:
        </h4>
        <ul className="space-y-2">
          <li className={`flex items-center text-sm ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
            <div className={`flex-shrink-0 w-5 h-5 mr-2 rounded-full flex items-center justify-center ${
              formData.password.length >= 8 ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <CheckCircle2 className={`w-4 h-4 ${formData.password.length >= 8 ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            Mínimo 8 caracteres
          </li>
          <li className={`flex items-center text-sm ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
            <div className={`flex-shrink-0 w-5 h-5 mr-2 rounded-full flex items-center justify-center ${
              /[A-Z]/.test(formData.password) ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <CheckCircle2 className={`w-4 h-4 ${/[A-Z]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            Al menos una mayúscula
          </li>
          <li className={`flex items-center text-sm ${/[0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
            <div className={`flex-shrink-0 w-5 h-5 mr-2 rounded-full flex items-center justify-center ${
              /[0-9]/.test(formData.password) ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <CheckCircle2 className={`w-4 h-4 ${/[0-9]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            Al menos un número
          </li>
        </ul>
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-indigo-50 rounded-xl p-4 border border-indigo-100"
      >
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-700">
            Una contraseña segura es fundamental para proteger tu cuenta. Nunca compartas tu contraseña con nadie y evita reutilizar contraseñas de otros sitios.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Componentes de renderizado de pasos
  const renderPersonalInfo = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Información Personal
      </motion.h3>

      {/* Input para nombre */}
      <motion.div variants={fadeInUp} className="relative">
        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="Nombre Completo"
          name="nombre"
          value={formData.nombre}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.nombre ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.nombre && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.nombre}</p>
        )}
      </motion.div>

      {/* Fecha de nacimiento */}
      <motion.div variants={fadeInUp} className="relative">
        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="date"
          name="fechaNacimiento"
          value={formData.fechaNacimiento}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.fechaNacimiento ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
          max={(() => {
            // Calcular fecha máxima (18 años atrás desde hoy)
            const today = new Date();
            today.setFullYear(today.getFullYear() - 18);
            return today.toISOString().split('T')[0];
          })()}
          onInput={(e) => {
            // Limitar a 4 dígitos en el año
            const value = e.target.value;
            const dateComponents = value.split('-');
            if (dateComponents[0] && dateComponents[0].length > 4) {
              dateComponents[0] = dateComponents[0].slice(0, 4);
              e.target.value = dateComponents.join('-');
            }
          }}
        />
        {errors.fechaNacimiento && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.fechaNacimiento}</p>
        )}
        <p className="text-xs text-gray-500 mt-1 ml-2">
          Debes tener al menos 18 años para registrarte
        </p>
      </motion.div>

      {/* Género */}
      <motion.div variants={fadeInUp} className="relative">
        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <select
          name="genero"
          value={formData.genero}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.genero ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        >
          <option value="">Seleccionar Género</option>
          <option value="masculino">Masculino</option>
          <option value="femenino">Femenino</option>
          <option value="otro">Otro</option>
        </select>
        {errors.genero && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.genero}</p>
        )}
      </motion.div>

      {/* Foto de perfil */}
      <motion.div variants={fadeInUp} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Foto de Perfil
        </label>
        <div className="flex items-center space-x-4">
          <div className={`h-20 w-20 rounded-full flex items-center justify-center border-2 overflow-hidden ${
            formData.fotoPreview ? 'border-indigo-300' : errors.fotoPerfil ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
          }`}>
            {formData.fotoPreview ? (
              <img src={formData.fotoPreview} alt="Preview" className="h-full w-full object-cover" />
            ) : (
              <Camera className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <div className="flex-1">
            <label className="cursor-pointer">
              <div className="px-4 py-2 text-sm bg-indigo-50 text-indigo-600 rounded-lg border border-indigo-200 hover:bg-indigo-100 transition-colors text-center">
                {formData.fotoPerfil ? 'Cambiar imagen' : 'Seleccionar imagen'}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            {errors.fotoPerfil && (
              <p className="mt-1 text-sm text-red-500">{errors.fotoPerfil}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Formatos: JPG, PNG, WEBP (máx. 5MB)
            </p>
          </div>
        </div>
      </motion.div>

      {/* Ubicación */}
      <motion.div variants={fadeInUp} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
          <MapPin className="h-4 w-4 text-indigo-500" />
          Ubicación
        </label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
          <LocationAutocomplete 
            onLocationSelect={handleLocationSelect}
            placeholder="Buscar ubicación..."
            className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
              errors.location ? 'border-red-300 bg-red-50' : 'border-gray-200'
            }`}
          />
          {errors.location && (
            <p className="mt-1 text-sm text-red-500 ml-2">{errors.location}</p>
          )}
        </div>
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-blue-50 rounded-xl p-4 border border-blue-100 mt-4"
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            Esta información será visible en tu perfil público. Tu ubicación ayudará a los clientes a encontrar agentes inmobiliarios en su zona.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Renderizado de información social del vendedor
  const renderSellerSocialInfo = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Información Social <span className="text-sm font-normal text-gray-500">(Opcional)</span>
      </motion.h3>

      <motion.div variants={fadeInUp} className="relative">
        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <div className="flex">
          <div className="bg-indigo-50 flex items-center px-3 border-2 border-r-0 border-gray-200 rounded-l-xl">
            <span className="text-gray-600 text-sm">+51</span>
          </div>
          <input
            type="tel"
            placeholder="WhatsApp"
            name="whatsapp"
            value={formData.whatsapp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 9);
              setFormData(prev => ({ ...prev, whatsapp: value }));
            }}
            className="w-full pl-4 pr-4 py-3 border-2 border-gray-200 rounded-r-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Facebook className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="Usuario de Facebook"
          name="facebook"
          value={formData.facebook}
          onChange={handleInputChange}
          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="Usuario de Instagram"
          name="instagram"
          value={formData.instagram}
          onChange={handleInputChange}
          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </motion.div>

      <motion.div variants={fadeInUp} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-indigo-500" />
          Sobre Mí
        </label>
        <textarea
          name="sobre_mi"
          value={formData.sobre_mi}
          onChange={handleInputChange}
          placeholder="Describe tu experiencia y especialización en el sector inmobiliario..."
          className={`w-full p-4 border-2 rounded-xl min-h-[120px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.sobre_mi ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.sobre_mi && (
          <p className="text-sm text-red-500 mt-1">{errors.sobre_mi}</p>
        )}
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-blue-50 rounded-xl p-4 border border-blue-100"
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            Compartir tus redes sociales puede ayudar a que los clientes te contacten más fácilmente. La sección "Sobre Mí" es donde puedes destacar tu perfil profesional.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Renderizado de experiencia y eslogan
  const renderExperienceAndSlogan = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Experiencia Profesional
      </motion.h3>

      <motion.div variants={fadeInUp} className="relative">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
          <Star className="h-4 w-4 text-indigo-500" />
          Slogan
        </label>
        <input
          type="text"
          placeholder="Ej: Tu aliado inmobiliario de confianza"
          name="slogan"
          value={formData.slogan}
          onChange={handleInputChange}
          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.slogan ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
          maxLength={100}
        />
        <div className="flex justify-between mt-1">
          <span className={`text-xs ${formData.slogan.length > 80 ? 'text-amber-500' : 'text-gray-500'}`}>
            {formData.slogan.length}/100 caracteres
          </span>
          {errors.slogan && (
            <p className="text-sm text-red-500">{errors.slogan}</p>
          )}
        </div>
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
          <Clock className="h-4 w-4 text-indigo-500" />
          Años de Experiencia
        </label>
        <input
          type="number"
          name="anos_experiencia"
          value={formData.anos_experiencia}
          onChange={handleInputChange}
          min="0"
          max="50"
          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.anos_experiencia ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.anos_experiencia && (
          <p className="text-sm text-red-500 mt-1">{errors.anos_experiencia}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
          <Briefcase className="h-4 w-4 text-indigo-500" />
          Descripción de tu Experiencia
        </label>
        <textarea
          name="experiencia"
          value={formData.experiencia}
          onChange={handleInputChange}
          placeholder="Detalla tu trayectoria profesional en el sector inmobiliario..."
          className={`w-full p-4 border-2 rounded-xl min-h-[120px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.experiencia ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.experiencia && (
          <p className="text-sm text-red-500 mt-1">{errors.experiencia}</p>
        )}
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-indigo-50 rounded-xl p-4 border border-indigo-100"
      >
        <div className="flex items-start gap-3">
          <Award className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-700">
            Tu experiencia es tu mejor carta de presentación. Los clientes valoran agentes inmobiliarios con trayectoria demostrable y conocimiento del mercado.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Renderizado de servicios y términos (paso final combinado)
  const renderServicesAndTerms = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Servicios y Finalización
      </motion.h3>

      {/* Servicios */}
      <motion.div variants={fadeInUp} className="space-y-3">
        <h4 className="text-md font-medium text-gray-700">
          Servicios que Ofreces <span className="text-sm font-normal text-gray-500">(Máximo 6)</span>
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            'Asesoría inmobiliaria',
            'Evaluación de propiedades',
            'Gestión de documentos',
            'Marketing inmobiliario',
            'Negociación',
            'Servicio post-venta',
            'Visitas guiadas',
            'Fotografía profesional'
          ].map((servicio) => (
            <motion.label
              key={servicio}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                formData.servicios.includes(servicio) 
                  ? 'border-indigo-500 bg-indigo-50 shadow-sm' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={formData.servicios.includes(servicio)}
                onChange={() => handleServiceChange(servicio)}
                className="hidden"
              />
              <div className={`flex-shrink-0 p-1 rounded-full mr-3 ${
                formData.servicios.includes(servicio) ? 'bg-indigo-100' : 'bg-gray-100'
              }`}>
                <CheckSquare
                  className={`w-5 h-5 ${
                    formData.servicios.includes(servicio) 
                      ? 'text-indigo-600' 
                      : 'text-gray-400'
                  }`}
                />
              </div>
              <span className={`${formData.servicios.includes(servicio) ? 'font-medium text-indigo-700' : 'text-gray-700'}`}>
                {servicio}
              </span>
            </motion.label>
          ))}
        </div>
        {errors.servicios && (
          <p className="text-red-500 text-sm mt-2 bg-red-50 p-3 rounded-lg">
            {errors.servicios}
          </p>
        )}
      </motion.div>

      {/* Teléfono opcional */}
      <motion.div variants={fadeInUp} className="space-y-3 mt-6">
        <h4 className="text-md font-medium text-gray-700">
          Información de Contacto <span className="text-sm font-normal text-gray-500">(Opcional)</span>
        </h4>
        <div className="flex gap-2">
          <select
            value={formData.prefijo_telefono}
            onChange={(e) => setFormData(prev => ({ ...prev, prefijo_telefono: e.target.value }))}
            className="pl-2 pr-8 py-3 border-2 rounded-xl bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
          >
            <option value="+51">🇵🇪 +51</option>
            <option value="+56">🇨🇱 +56</option>
            <option value="+57">🇨🇴 +57</option>
            <option value="+54">🇦🇷 +54</option>
            <option value="+593">🇪🇨 +593</option>
          </select>

          <div className="relative flex-1">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
            <input
              type="tel"
              placeholder="Número de teléfono (opcional)"
              name="telefono"
              value={formData.telefono}
              onChange={(e) => {
                // Permitir solo números y limitar a 9 dígitos
                const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                setFormData(prev => ({ 
                  ...prev, 
                  telefono: value 
                }));
                
                // Limpiar errores
                if (errors.telefono) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.telefono;
                    return newErrors;
                  });
                }
              }}
              className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl transition-colors duration-300 ${
                errors.telefono ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            />
          </div>
        </div>
        {errors.telefono && (
          <p className="text-red-500 text-sm bg-red-50 p-2 rounded-lg">
            {errors.telefono}
          </p>
        )}
        <p className="text-xs text-gray-500">
          El teléfono es opcional. Si lo proporciona, debe tener 9 dígitos válidos.
        </p>
      </motion.div>

      {/* Términos y condiciones */}
      <motion.div 
        variants={fadeInUp}
        className="space-y-4 mt-6 bg-gray-50 p-6 rounded-xl border border-gray-200"
      >
        <h4 className="font-medium text-gray-800">Términos Legales</h4>
        <p className="text-sm text-gray-600">
          Para completar tu registro, es necesario que aceptes nuestros términos legales:
        </p>
        
        <div className="space-y-3">
          {/* Checkbox para Términos y Condiciones */}
          <div className={`flex items-start gap-3 ${errors.terms ? 'pb-1' : ''}`}>
            <div className="flex items-center h-5 mt-1">
              <input
                id="terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => {
                  setAcceptedTerms(e.target.checked);
                  if (errors.terms && e.target.checked) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.terms;
                      return newErrors;
                    });
                  }
                }}
                className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                  errors.terms ? 'border-red-300' : ''
                }`}
              />
            </div>
            <div>
              <label htmlFor="terms" className={`text-sm font-medium ${errors.terms ? 'text-red-700' : 'text-gray-700'}`}>
                Acepto los <a 
                  href="/terminos" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-indigo-600 hover:text-indigo-800 hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('/terminos', '_blank');
                  }}
                >
                  Términos y Condiciones
                </a>
              </label>
              {errors.terms && (
                <p className="text-sm text-red-600">{errors.terms}</p>
              )}
            </div>
          </div>
          
          {/* Checkbox para Política de Privacidad */}
          <div className={`flex items-start gap-3 ${errors.privacy ? 'pb-1' : ''}`}>
            <div className="flex items-center h-5 mt-1">
              <input
                id="privacy"
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => {
                  setAcceptedPrivacy(e.target.checked);
                  if (errors.privacy && e.target.checked) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.privacy;
                      return newErrors;
                    });
                  }
                }}
                className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                  errors.privacy ? 'border-red-300' : ''
                }`}
              />
            </div>
            <div>
              <label htmlFor="privacy" className={`text-sm font-medium ${errors.privacy ? 'text-red-700' : 'text-gray-700'}`}>
                Acepto la <a 
                  href="/privacidad" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-indigo-600 hover:text-indigo-800 hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('/privacidad', '_blank');
                  }}
                >
                  Política de Privacidad
                </a>
              </label>
              {errors.privacy && (
                <p className="text-sm text-red-600">{errors.privacy}</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="text-xs text-gray-500 pt-2">
          Al aceptar, confirmas que has leído y estás de acuerdo con nuestros términos legales y entiendes cómo procesamos tus datos.
        </div>
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-indigo-50 rounded-xl p-4 border border-indigo-100"
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-700">
            Los servicios que selecciones aparecerán en tu perfil público. Esto ayuda a los clientes potenciales a entender mejor qué puedes ofrecerles.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <BaseRegister
      currentStep={currentStep}
      totalSteps={totalSteps}
      handleNext={handleNext}
      handleBack={handleBack}
      handleFinish={handleFinish}
      loading={loading}
      title="Registro de Vendedor"
      errors={errors}
    >
      {renderStep()}
    </BaseRegister>
  );
};

export default SellerRegister;